/**
 * The application scrolls inside `<nz-content>`, not the window, so the router's own scroll
 * restoration never sees the offset: it watches the viewport, which never moves. A page that
 * sends the user somewhere else — a chart bar opening a record — remembers its own offset here
 * and puts it back when the user comes back.
 */

const STORAGE_KEY = 'scroll-memory';

interface RememberedScroll {
  url: string;
  top: number;
}

export function getScrollContainer(): HTMLElement | null {
  return document.querySelector('nz-content');
}

/** Only the path is kept: the query parameters say nothing about how far down the page is. */
const pathOf = (url: string) => url.split('?')[0];

/** Notes where the page stands, together with the page it belongs to. */
export function rememberScroll(key: string, url: string): void {
  const top = getScrollContainer()?.scrollTop ?? 0;
  try {
    sessionStorage.setItem(
      `${STORAGE_KEY}:${key}`,
      JSON.stringify({ url: pathOf(url), top } satisfies RememberedScroll)
    );
  } catch {
    // A browser refusing session storage simply does without the restoration.
  }
}

/**
 * Reads back an offset noted for `url` and forgets it, so that it is only ever used for the
 * return trip it was taken for and not for a later visit to the same page.
 */
export function takeRememberedScroll(key: string, url: string): number | undefined {
  try {
    const stored = sessionStorage.getItem(`${STORAGE_KEY}:${key}`);
    sessionStorage.removeItem(`${STORAGE_KEY}:${key}`);
    if (!stored) {
      return undefined;
    }
    const remembered = JSON.parse(stored) as RememberedScroll;
    return remembered.url === pathOf(url) ? remembered.top : undefined;
  } catch {
    return undefined;
  }
}

/** A little air above the element, so it does not sit flush against the top of the view. */
const SCROLL_TO_MARGIN = 16;

/** How many frames the element has to hold still before the scroll is considered landed. */
const SETTLED_FRAMES = 10;

/**
 * A page whose heading stays pinned to the top of the container marks it with this attribute,
 * so that scrolling to a section stops below the band rather than under it.
 */
const STICKY_TOP_SELECTOR = '[data-sticky-top]';

/**
 * Brings an element into view inside the scrolling container, and keeps it there while the page
 * settles. Two things make this more than a one-shot scroll: a record page renders its sections
 * only once the record has arrived, and a section that fetches its own data is present but empty
 * — height zero — well before it is worth scrolling to. So the element is only aimed at once it
 * has a height, and the aim is corrected until it stops moving. Gives up if the user scrolls
 * first, or after `timeoutMs`.
 */
export function scrollToWhenReady(selector: string, timeoutMs = 5000): void {
  const container = getScrollContainer();
  if (!container) {
    return;
  }

  const start = performance.now();
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  const events = ['wheel', 'touchstart', 'keydown'] as const;
  events.forEach((event) =>
    container.addEventListener(event, cancel, { passive: true, once: true })
  );
  const stop = () =>
    events.forEach((event) => container.removeEventListener(event, cancel));

  let settled = 0;
  const step = () => {
    if (cancelled) {
      stop();
      return;
    }

    const element = document.querySelector(selector);
    const rect = element?.getBoundingClientRect();
    // A section still waiting for its own data is there but empty: nothing to aim at yet.
    if (rect && rect.height > 0) {
      const stickyHeight =
        container
          .querySelector(STICKY_TOP_SELECTOR)
          ?.getBoundingClientRect().height ?? 0;
      const offset = rect.top - container.getBoundingClientRect().top;
      const correction = offset - SCROLL_TO_MARGIN - stickyHeight;
      container.scrollTop += correction;
      settled = Math.abs(correction) <= 1 ? settled + 1 : 0;
    }

    if (settled < SETTLED_FRAMES && performance.now() - start < timeoutMs) {
      requestAnimationFrame(step);
    } else {
      stop();
    }
  };
  requestAnimationFrame(step);
}

/**
 * Scrolls back to `top`, and keeps trying while the page is still filling out: the charts arrive
 * from several requests, and until they do the content is too short to reach the offset. Gives up
 * once it lands, once the user takes over, or after `timeoutMs`.
 */
export function restoreScrollTop(top: number, timeoutMs = 2000): void {
  const element = getScrollContainer();
  if (!element || top <= 0) {
    return;
  }

  const start = performance.now();
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  const events = ['wheel', 'touchstart', 'keydown'] as const;
  events.forEach((event) =>
    element.addEventListener(event, cancel, { passive: true, once: true })
  );
  const stop = () =>
    events.forEach((event) => element.removeEventListener(event, cancel));

  const step = () => {
    if (cancelled) {
      stop();
      return;
    }
    element.scrollTop = top;
    if (
      Math.abs(element.scrollTop - top) > 1 &&
      performance.now() - start < timeoutMs
    ) {
      requestAnimationFrame(step);
    } else {
      stop();
    }
  };
  requestAnimationFrame(step);
}
